import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge, Box, Button, Chip, Flex, Group, Loader, MultiSelect, Paper, Select, Stack, Text, TextInput } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useForm, yupResolver } from "@mantine/form";
import { CalendarMonthOutlined, GroupOutlined, LocalHospitalOutlined, PersonOutlined } from "@mui/icons-material";
import { SvgIcon } from "@mui/material";
import { useContext, useEffect, useRef } from "react";
import { DAYS, TIMES } from "../../datasets/days_available";
import { GRADE_LEVELS } from "../../datasets/grade_level";
import { PREFERRED_LANGUAGES } from "../../datasets/preferred_languages";
import { useCreatePatientMutation, useEntityListQuery, useGetPatientQuery, useGetPostalCodeQuery, useGetTherapySubCategoryGroupsQuery, useUpdatePatientMutation } from "../../graphql/graphql";
import { Length } from "../../utils/Validation.utils";
import { calculateAge } from "../../utils/Date.utils";
import { showErrorNotification, showSuccessNotification } from "../../shared/Notifications";
import { AppContext } from "../../shared/PrivateRoutes";
import { useTherapproveDrawers } from "../../shared/TherapproveOverlays";
import { titleCase } from "../../utils/String.utils";
import { PatientDrawerInitialValues, PatientDrawerSchema } from "./PatientDrawer.validation";
// ─── Subcomponents ──────────────────────────────────────────────────
const Section = ({ icon, title, children }) => (_jsx(Paper, { radius: "md", p: "md", withBorder: true, style: { borderColor: "var(--mantine-color-gray-2)" }, children: _jsxs(Stack, { gap: "sm", children: [_jsxs(Group, { gap: "xs", children: [_jsx(SvgIcon, { component: icon, style: {
                            fontSize: "1rem",
                            color: "var(--mantine-color-primary-5)"
                        } }), _jsx(Text, { size: "sm", fw: 600, c: "dimmed", children: title })] }), children] }) }));
const FieldLabel = ({ children }) => (_jsx(Text, { size: "xs", fw: 500, c: "dimmed", children: children }));
// ─── Main component ─────────────────────────────────────────────────
export const PatientDrawerFormContent = ({ patientId, caregiverId, active = true, onSuccess, onDirtyChange }) => {
    // Context
    const { isTherapproveAdmin } = useContext(AppContext);
    const { caregiver: caregiverDrawer } = useTherapproveDrawers();
    const isCreate = !patientId;
    // Org admins and above can edit; read-only for others
    const canEdit = true;
    const firstNameRef = useRef(null);
    const lastNameRef = useRef(null);
    // Mutations
    const [{ fetching: createFetching }, createPatient] = useCreatePatientMutation();
    const [{ fetching: updateFetching }, updatePatient] = useUpdatePatientMutation();
    // Queries
    const [{ data: patientData }] = useGetPatientQuery({
        requestPolicy: "cache-and-network",
        variables: { patientId: patientId },
        pause: !patientId || !active
    });
    const patient = patientData?.patients?.patient;
    const caregiver = patient?.caregiver;
    const [{ data: entityListData }] = useEntityListQuery({
        pause: !active
    });
    const providers = entityListData?.users?.users ?? [];
    const conditions = entityListData?.conditions?.conditionsList ?? [];
    const insurances = entityListData?.insurances?.insurancesList ?? [];
    const therapyCategories = entityListData?.therapyCategories?.therapyCategoriesList ?? [];
    // Form
    const form = useForm({
        initialValues: PatientDrawerInitialValues,
        validate: yupResolver(PatientDrawerSchema)
    });
    const isDirty = form.isDirty();
    const fetching = createFetching || updateFetching;
    // Fetch city/state when the postal code changes
    const [{ data: postalCodeData, fetching: fetchingPostalCode }] = useGetPostalCodeQuery({
        variables: { postalCode: form.values.postalCode || "" },
        pause: (form.values.postalCode || "").length < Length.PostalCode
    });
    const postalCodeInfo = postalCodeData?.postalCodes?.postalCode;
    // Therapy sub-categories
    const [{ data: subCategoryData, fetching: subCatFetching }] = useGetTherapySubCategoryGroupsQuery({
        variables: {
            therapyCategoryIds: form.values.therapyCategoryIds ?? []
        },
        pause: !form.values.therapyCategoryIds?.length || !active
    });
    const therapySubCategories = subCategoryData?.therapySubCategories?.therapySubCategoryGroups ?? [];
    // Effects
    useEffect(() => {
        onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);
    // Populate form from loaded patient data or reset for create
    useEffect(() => {
        if (!active)
            return;
        if (isCreate) {
            form.setValues(PatientDrawerInitialValues);
        }
        else if (patient) {
            form.setValues({
                firstName: patient.firstName,
                lastName: patient.lastName,
                postalCode: patient.postalCode ?? "",
                dateOfBirth: patient.dateOfBirth
                    ? new Date(patient.dateOfBirth)
                    : null,
                gradeLevel: patient.gradeLevel ?? null,
                preferredLanguages: patient.preferredLanguages ?? [],
                daysAvailable: patient.daysAvailable ?? [],
                timeAvailable: patient.timeAvailable ?? [],
                providerIds: patient.providers?.map((p) => p.userId) ?? [],
                conditionIds: patient.conditionList?.map((c) => c.value) ?? [],
                insuranceIds: patient.insuranceList?.map((i) => i.value) ?? [],
                therapyCategoryIds: patient.therapyCategoryList?.map((t) => t.value) ?? [],
                therapySubCategoryIds: patient.therapySubCategoryList?.map((t) => t.value) ?? []
            });
        }
        form.resetDirty();
    }, [active, patientId, patient]);
    // Handlers
    const handleSubmit = async (event) => {
        event.preventDefault();
        // Sync contentEditable name fields before validating — the user
        // may click Save without blurring the field first.
        const currentFirst = (firstNameRef.current?.textContent ?? "").trim();
        const currentLast = (lastNameRef.current?.textContent ?? "").trim();
        form.setFieldValue("firstName", currentFirst);
        form.setFieldValue("lastName", currentLast);
        const validation = form.validate();
        if (validation.hasErrors) {
            showErrorNotification("Please fill in all required fields.");
            return;
        }
        const values = form.getValues();
        try {
            if (isCreate) {
                const resolvedCaregiverId = caregiverId;
                if (!resolvedCaregiverId) {
                    showErrorNotification("A primary contact is required.");
                    return;
                }
                const result = await createPatient({
                    input: {
                        caregiverId: resolvedCaregiverId,
                        firstName: values.firstName,
                        lastName: values.lastName,
                        postalCode: values.postalCode || "00000",
                        dateOfBirth: values.dateOfBirth?.toISOString() ?? new Date().toISOString(),
                        therapyCategoryIds: values.therapyCategoryIds.length
                            ? values.therapyCategoryIds
                            : [],
                        preferredLanguages: values.preferredLanguages.length
                            ? values.preferredLanguages
                            : undefined,
                        daysAvailable: values.daysAvailable.length
                            ? values.daysAvailable
                            : undefined,
                        timeAvailable: values.timeAvailable.length
                            ? values.timeAvailable
                            : undefined,
                        providerIds: values.providerIds.length
                            ? values.providerIds
                            : undefined,
                        conditionIds: values.conditionIds.length
                            ? values.conditionIds
                            : undefined,
                        insuranceIds: values.insuranceIds.length
                            ? values.insuranceIds
                            : undefined,
                        therapySubCategoryIds: values.therapySubCategoryIds.length
                            ? values.therapySubCategoryIds
                            : undefined,
                        gradeLevel: values.gradeLevel || undefined
                    }
                });
                if (!result.data?.patients?.createPatient) {
                    throw new Error("Failed to create client.");
                }
                showSuccessNotification(`${values.firstName} ${values.lastName} was added.`);
            }
            else {
                const result = await updatePatient({
                    input: {
                        patientId: patientId,
                        firstName: values.firstName,
                        lastName: values.lastName,
                        postalCode: values.postalCode || undefined,
                        dateOfBirth: values.dateOfBirth?.toISOString() ?? undefined,
                        gradeLevel: values.gradeLevel || undefined,
                        preferredLanguages: values.preferredLanguages,
                        daysAvailable: values.daysAvailable,
                        timeAvailable: values.timeAvailable,
                        providerIds: values.providerIds,
                        conditionIds: values.conditionIds,
                        insuranceIds: values.insuranceIds,
                        therapyCategoryIds: values.therapyCategoryIds,
                        therapySubCategoryIds: values.therapySubCategoryIds
                    }
                });
                if (!result.data?.patients?.updatePatient) {
                    throw new Error("Failed to update client.");
                }
                showSuccessNotification(`${values.firstName} ${values.lastName} was updated.`);
            }
            form.resetDirty();
            onSuccess?.();
        }
        catch (error) {
            console.error(error);
            showErrorNotification();
        }
    };
    // Computed
    const age = form.values.dateOfBirth
        ? calculateAge(form.values.dateOfBirth.toISOString())
        : null;
    // Select all text on focus so typing replaces the existing value
    const selectAllOnFocus = (e) => {
        const selection = window.getSelection();
        if (!selection)
            return;
        const range = document.createRange();
        range.selectNodeContents(e.currentTarget);
        selection.removeAllRanges();
        selection.addRange(range);
    };
    // Strip leftover <br> / empty text nodes so :empty works for the placeholder
    const cleanEmpty = (e) => {
        const el = e.currentTarget;
        if (!el.textContent?.trim()) {
            el.innerHTML = "";
        }
    };
    // Unstyled input styles for fields that can't use contentEditable
    const unstyled = {
        input: {
            border: "none",
            backgroundColor: "transparent",
            padding: 0,
            minHeight: "auto"
        }
    };
    return (_jsxs(Stack, { gap: 0, style: { flex: 1, minHeight: 0 }, children: [_jsx("div", { style: { flex: 1, overflowY: "auto", overflowX: "hidden" }, children: _jsxs(Stack, { gap: "md", pb: "md", children: [_jsx(Box, { py: "lg", px: "md", style: { backgroundColor: "var(--mantine-color-primary-0)" }, children: _jsxs(Stack, { gap: 4, children: [_jsxs(Group, { gap: 6, wrap: "wrap", children: [_jsx(Text, { ref: firstNameRef, fw: 700, style: {
                                                    fontSize: "1.5rem",
                                                    whiteSpace: "nowrap",
                                                    ...(form.errors.firstName ? { outline: "2px solid var(--mantine-color-red-5)", borderRadius: 4 } : {})
                                                }, c: "gray.9", contentEditable: canEdit, suppressContentEditableWarning: true, "data-editable-field": true, "data-placeholder": "First", onFocus: selectAllOnFocus, onInput: cleanEmpty, onBlur: (e) => {
                                                    form.setFieldValue("firstName", (e.currentTarget.textContent ?? "").trim());
                                                }, children: form.values.firstName || null }), _jsx(Text, { ref: lastNameRef, fw: 700, style: {
                                                    fontSize: "1.5rem",
                                                    whiteSpace: "nowrap",
                                                    ...(form.errors.lastName ? { outline: "2px solid var(--mantine-color-red-5)", borderRadius: 4 } : {})
                                                }, c: "gray.9", contentEditable: canEdit, suppressContentEditableWarning: true, "data-editable-field": true, "data-placeholder": "Last", onFocus: selectAllOnFocus, onInput: cleanEmpty, onBlur: (e) => {
                                                    form.setFieldValue("lastName", (e.currentTarget.textContent ?? "").trim());
                                                }, children: form.values.lastName || null })] }), _jsxs(Group, { gap: "xs", children: [age && (_jsxs(Badge, { size: "sm", variant: "light", color: "blue", children: [age.years, "y ", age.months, "m"] })), caregiver && (_jsxs(Text, { size: "xs", c: "dimmed", style: { cursor: "pointer" }, onClick: () => {
                                                    if (caregiver.userId) {
                                                        caregiverDrawer.open({
                                                            caregiverId: caregiver.userId
                                                        });
                                                    }
                                                }, children: [caregiver.firstName, " ", caregiver.lastName] }))] })] }) }), _jsxs(Stack, { gap: "sm", px: "md", children: [_jsx(Section, { icon: PersonOutlined, title: "Personal Info", children: _jsxs(Stack, { gap: "sm", children: [_jsxs(Flex, { gap: "md", children: [_jsxs(Stack, { gap: 4, style: { flex: 1 }, children: [_jsx(FieldLabel, { children: "Date of Birth" }), _jsx(DatePickerInput, { variant: "unstyled", placeholder: "Select date", disabled: !canEdit, size: "xs", clearable: true, styles: unstyled, value: form.values.dateOfBirth, onChange: (value) => form.setFieldValue("dateOfBirth", value) })] }), _jsxs(Stack, { gap: 4, style: { flex: 1 }, children: [_jsx(FieldLabel, { children: "Location" }), _jsxs(Flex, { align: "center", gap: "xs", wrap: "nowrap", children: [_jsx(TextInput, { variant: "unstyled", placeholder: "Zip code", disabled: !canEdit, maxLength: 5, size: "xs", styles: {
                                                                            ...unstyled,
                                                                            input: {
                                                                                ...unstyled.input,
                                                                                textAlign: "left"
                                                                            }
                                                                        }, ...form.getInputProps("postalCode") }), fetchingPostalCode && (_jsx(Loader, { size: 14 })), !fetchingPostalCode && postalCodeInfo && (_jsxs(Text, { size: "xs", c: "dimmed", children: [titleCase(postalCodeInfo.city), ", ", postalCodeInfo.stateId] }))] })] })] }), _jsxs(Flex, { gap: "md", children: [_jsxs(Stack, { gap: 4, style: { flex: 1 }, children: [_jsx(FieldLabel, { children: "Preferred Languages" }), _jsx(MultiSelect, { variant: "pill", placeholder: "Select languages", disabled: !canEdit, clearable: true, searchable: true, size: "xs", data: PREFERRED_LANGUAGES, styles: unstyled, ...form.getInputProps("preferredLanguages") })] }), _jsxs(Stack, { gap: 4, style: { flex: 1 }, children: [_jsx(FieldLabel, { children: "Grade Level" }), _jsx(Select, { variant: "unstyled", placeholder: "Select grade", disabled: !canEdit, clearable: true, searchable: true, size: "xs", data: GRADE_LEVELS, styles: {
                                                                    ...unstyled,
                                                                    input: {
                                                                        ...unstyled.input,
                                                                        textAlign: "left"
                                                                    }
                                                                }, ...form.getInputProps("gradeLevel") })] })] })] }) }), _jsx(Section, { icon: CalendarMonthOutlined, title: "Availability", children: _jsxs(Stack, { gap: "sm", children: [_jsxs(Stack, { gap: 4, children: [_jsx(FieldLabel, { children: "Days" }), _jsx(Chip.Group, { multiple: true, value: form.values.daysAvailable, onChange: (value) => form.setFieldValue("daysAvailable", value), children: _jsx(Flex, { gap: 6, wrap: "wrap", children: DAYS.map((day) => (_jsx(Chip, { value: day, size: "xs", disabled: !canEdit, children: day.slice(0, 3) }, day))) }) })] }), _jsxs(Stack, { gap: 4, children: [_jsx(FieldLabel, { children: "Times" }), _jsx(Chip.Group, { multiple: true, value: form.values.timeAvailable, onChange: (value) => form.setFieldValue("timeAvailable", value), children: _jsx(Flex, { gap: 6, wrap: "wrap", children: TIMES.map((time) => (_jsx(Chip, { value: time, size: "xs", disabled: !canEdit, children: time }, time))) }) })] })] }) }), _jsx(Section, { icon: LocalHospitalOutlined, title: "Clinical", children: _jsxs(Stack, { gap: "sm", children: [_jsxs(Stack, { gap: 4, children: [_jsx(FieldLabel, { children: "Diagnoses" }), _jsx(MultiSelect, { variant: "pill", placeholder: "Select diagnoses", disabled: !canEdit, clearable: true, searchable: true, size: "xs", data: conditions, styles: unstyled, ...form.getInputProps("conditionIds") })] }), _jsxs(Stack, { gap: 4, children: [_jsx(FieldLabel, { children: "Therapies Needed" }), _jsx(MultiSelect, { variant: "pill", placeholder: "Select therapies", disabled: !canEdit, clearable: true, searchable: true, size: "xs", data: therapyCategories, styles: unstyled, ...form.getInputProps("therapyCategoryIds"), onChange: (value) => {
                                                            form.setValues({
                                                                ...form.values,
                                                                therapyCategoryIds: value,
                                                                therapySubCategoryIds: []
                                                            });
                                                        } })] }), form.values.therapyCategoryIds.length > 0 && (_jsxs(Stack, { gap: 4, children: [_jsx(FieldLabel, { children: "Skills / Interventions" }), _jsx(MultiSelect, { variant: "pill", placeholder: "Select skills", disabled: !canEdit, clearable: true, searchable: true, size: "xs", rightSection: subCatFetching ? _jsx(Loader, { size: 14 }) : null, data: therapySubCategories, styles: unstyled, ...form.getInputProps("therapySubCategoryIds") })] })), _jsxs(Stack, { gap: 4, children: [_jsx(FieldLabel, { children: "Insurances" }), _jsx(MultiSelect, { variant: "pill", placeholder: "Select insurances", disabled: !canEdit, clearable: true, searchable: true, limit: 50, size: "xs", data: insurances, styles: unstyled, ...form.getInputProps("insuranceIds") })] })] }) }), _jsx(Section, { icon: GroupOutlined, title: "Care Team", children: _jsxs(Stack, { gap: "sm", children: [caregiver && (_jsxs(Stack, { gap: 4, children: [_jsx(FieldLabel, { children: "Primary Contact" }), _jsxs(Text, { size: "sm", fw: 600, c: "gray.8", style: { cursor: "pointer" }, onClick: () => {
                                                            if (caregiver.userId) {
                                                                caregiverDrawer.open({
                                                                    caregiverId: caregiver.userId
                                                                });
                                                            }
                                                        }, children: [caregiver.firstName, " ", caregiver.lastName] })] })), _jsxs(Stack, { gap: 4, children: [_jsx(FieldLabel, { children: "Assigned Providers" }), _jsx(MultiSelect, { variant: "pill", placeholder: "Select providers", disabled: !canEdit, clearable: true, searchable: true, size: "xs", data: providers.map((p) => ({
                                                            value: p.userId,
                                                            label: `${p.firstName} ${p.lastName}`
                                                        })), styles: unstyled, ...form.getInputProps("providerIds") })] })] }) })] })] }) }), canEdit && isDirty && (_jsxs(Flex, { gap: "sm", px: "md", py: "sm", style: {
                    borderTop: "1px solid var(--mantine-color-gray-2)",
                    backgroundColor: "var(--mantine-color-body)"
                }, children: [_jsx(Button, { variant: "filled", loading: fetching, onClick: handleSubmit, children: isCreate ? "Add Client" : "Save Changes" }), _jsx(Button, { variant: "subtle", c: "dimmed", onClick: () => {
                            form.reset();
                            form.resetDirty();
                        }, children: "Cancel" })] }))] }));
};
